import { useEffect, useMemo, useState } from "react";
import { Archive, Check, Clock3, Lock, Pencil, RotateCcw, X } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  FilterBar,
  KorwilBadge,
  Pagination,
  SectionTitle,
  SelectFilter,
  StatusBadge,
  TableShell,
  Td,
  TextFilter,
  Th,
  fmtDate,
  isLate,
} from "@/components/Shared";

export default function Jadwal() {
  const { korwilFilter, user } = useAuth();
  const canManage = ["SUPER_ADMIN", "KORWIL", "ADMIN_KORWIL"].includes(user?.role);
  const [archiveView, setArchiveView] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editCode, setEditCode] = useState("");
  const [lookupTech, setLookupTech] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [showTechList, setShowTechList] = useState(false);
  const [lookupState, setLookupState] = useState("idle");
  const [historyItem, setHistoryItem] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [opts, setOpts] = useState({});
  const [f, setF] = useState({
    kabupaten: "",
    kecamatan: "",
    npsn: "",
    sekolah: "",
    status: "",
    q: "",
    tanggal_dari: "",
    tanggal_sampai: "",
  });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [res, setRes] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/filters/options").then(({ data }) => setOpts(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { ...f, korwil: korwilFilter || undefined, arsip: archiveView, page, per_page: perPage };
    Object.keys(params).forEach((k) => !params[k] && delete params[k]);
    api
      .get("/jadwal", { params })
      .then(({ data }) => setRes(data))
      .catch(() => setRes({ items: [], total: 0 }))
      .finally(() => setLoading(false));
  }, [f, korwilFilter, page, perPage, archiveView]);

  useEffect(() => {
    if (!editItem) return;
    const term = editCode.trim();
    if (lookupTech?.kode_teknisi === term) { setSearchResults([]); return; }
    if (term.length < 2) { setSearchResults([]); setLookupState("idle"); return; }
    let cancelled = false;
    setLookupState("loading");
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/jadwal/teknisi-cari", {
          params: { id_transaksi: editItem.id_transaksi, q: term },
        });
        if (cancelled) return;
        setSearchResults(data?.items || []);
        setLookupState((data?.items || []).length ? "found" : "not-found");
      } catch (e) {
        if (!cancelled) {
          setSearchResults([]);
          setLookupState(e?.response?.status === 403 ? "forbidden" : "error");
        }
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [editCode, editItem, lookupTech]);

  const openEdit = (item) => {
    setEditItem(item);
    setEditCode("");
    setLookupTech(null);
    setSearchResults([]);
    setLookupState("idle");
    setShowTechList(false);
  };

  const chooseTechnician = (tech) => {
    setLookupTech(tech);
    setEditCode(tech.kode_teknisi || "");
    setSearchResults([]);
    setLookupState("selected");
    setShowTechList(false);
  };

  const openHistory = async (item) => {
    setHistoryItem(item);
    setHistoryRows([]);
    setHistoryLoading(true);
    try {
      const { data } = await api.get("/jadwal/" + encodeURIComponent(item.id_transaksi) + "/history");
      setHistoryRows(data?.items || []);
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Gagal memuat riwayat perubahan jadwal.");
      setHistoryItem(null);
    } finally {
      setHistoryLoading(false);
    }
  };

  const reloadSchedules = async () => {
    setLoading(true);
    try {
      const params = { ...f, korwil: korwilFilter || undefined, arsip: archiveView, page, per_page: perPage };
      Object.keys(params).forEach((k) => params[k] === "" || params[k] === undefined || params[k] === null ? delete params[k] : null);
      const { data } = await api.get("/jadwal", { params }); setRes(data);
    } catch { setRes({ items: [], total: 0 }); } finally { setLoading(false); }
  };

  const saveEdit = async () => {
    if (!editItem || !lookupTech?.kode_teknisi || lookupTech.kode_teknisi !== editCode.trim()) return;
    setSaving(true);
    try {
      await api.patch("/jadwal/" + encodeURIComponent(editItem.id_transaksi), { teknisi_pusat_kode: lookupTech.kode_teknisi });
      setEditItem(null); setLookupTech(null); await reloadSchedules();
    } catch (e) { window.alert(e?.response?.data?.detail || "Gagal menyimpan perubahan teknisi."); }
    finally { setSaving(false); }
  };

  const toggleArchive = async (item, restore = false) => {
    const action = restore ? "mengaktifkan kembali" : "mengarsipkan";
    if (!window.confirm("Yakin " + action + " jadwal " + item.id_transaksi + " — " + item.sekolah + "?")) return;
    try {
      const url = "/jadwal/" + encodeURIComponent(item.id_transaksi) + (restore ? "/aktifkan" : "/arsip");
      await api.post(url); await reloadSchedules();
    } catch (e) { window.alert(e?.response?.data?.detail || ("Gagal " + action + " jadwal.")); }
  };
  const set = (k) => (v) => {
    setPage(1);
    setF((p) => ({ ...p, [k]: v }));
  };

  // Kelompokkan tampilan berdasarkan NPSN terlebih dahulu, lalu ID transaksi.
  // Dengan pola ini, sekolah yang memiliki 2-3 ID transaksi akan selalu tampil
  // berurutan sehingga lebih mudah dicek.
  const sortedItems = useMemo(() => {
    return [...(res.items || [])].sort((a, b) => {
      const npsnA = String(a.npsn ?? "").trim();
      const npsnB = String(b.npsn ?? "").trim();
      const npsnCompare = npsnA.localeCompare(npsnB, "id", { numeric: true, sensitivity: "base" });
      if (npsnCompare !== 0) return npsnCompare;

      const idA = String(a.id_transaksi ?? "").trim();
      const idB = String(b.id_transaksi ?? "").trim();
      return idA.localeCompare(idB, "id", { numeric: true, sensitivity: "base" });
    });
  }, [res.items]);

  return (
    <div data-testid="page-jadwal">
      <SectionTitle
        title="Jadwal Pusat"
        subtitle="Jadwal instalasi resmi dari PYX Pusat — diurutkan berdasarkan NPSN untuk memudahkan pengecekan ID transaksi"
        right={
          <div className="flex items-center gap-2">
            {canManage && <button data-testid="jadwal-archive-toggle" onClick={() => { setArchiveView((v) => !v); setPage(1); }} className="flex items-center gap-1.5 rounded border border-slate-700 px-3 py-1.5 text-xs font-semibold text-slate-300 hover:bg-slate-800"><Archive className="h-3.5 w-3.5" /> {archiveView ? "Lihat Jadwal Aktif" : "Lihat Arsip"}</button>}
            {!canManage && <span className="flex items-center gap-1.5 rounded border border-slate-700 bg-slate-900 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400"><Lock className="h-3 w-3" /> Read Only</span>}
          </div>
        }
      />

      <FilterBar>
        <TextFilter label="Cari" testId="jadwal-search" value={f.q} onChange={set("q")} placeholder="id_transaksi / NPSN / sekolah / teknisi" />
        <SelectFilter label="Kabupaten/Kota" testId="jadwal-filter-kabupaten" value={f.kabupaten} onChange={set("kabupaten")} options={opts.kabupaten_unit} />
        <SelectFilter label="Kecamatan" testId="jadwal-filter-kecamatan" value={f.kecamatan} onChange={set("kecamatan")} options={opts.kecamatan} />
        <SelectFilter label="Status Pusat" testId="jadwal-filter-status" value={f.status} onChange={set("status")} options={opts.status_pusat} />
        <TextFilter label="NPSN" testId="jadwal-filter-npsn" value={f.npsn} onChange={set("npsn")} placeholder="NPSN" />
        <TextFilter label="Sekolah" testId="jadwal-filter-sekolah" value={f.sekolah} onChange={set("sekolah")} placeholder="Nama sekolah" />
        <TextFilter label="Dari" type="date" testId="jadwal-filter-dari" value={f.tanggal_dari} onChange={set("tanggal_dari")} />
        <TextFilter label="Sampai" type="date" testId="jadwal-filter-sampai" value={f.tanggal_sampai} onChange={set("tanggal_sampai")} />
        <button
          data-testid="jadwal-reset"
          onClick={() =>
            setF({ kabupaten: "", kecamatan: "", npsn: "", sekolah: "", status: "", q: "", tanggal_dari: "", tanggal_sampai: "" })
          }
          className="rounded border border-slate-700 px-3 py-1.5 text-xs text-slate-300 transition-colors hover:bg-slate-800 hover:text-white"
        >
          Reset
        </button>
      </FilterBar>

      <TableShell testId="jadwal-table" loading={loading} empty={sortedItems.length === 0}>
        <thead>
          <tr>
            <Th>NPSN ↑</Th>
            <Th>id_transaksi</Th>
            <Th>Sekolah</Th>
            <Th>Provinsi</Th>
            <Th>Kabupaten/Kota</Th>
            <Th>Kecamatan</Th>
            <Th>Unit</Th>
            <Th>Korwil</Th>
            <Th>Jadwal</Th>
            <Th>Teknisi Pusat</Th>
            <Th>No HP Teknisi</Th>
            <Th>Status Pusat</Th>
            {canManage && <Th>Aksi</Th>}
          </tr>
        </thead>
        <tbody>
          {sortedItems.map((u) => (
            <tr
              key={u.id_transaksi}
              data-testid={`jadwal-row-${u.id_transaksi}`}
              className="border-b border-slate-800/70 transition-colors hover:bg-slate-800/50"
            >
              <Td className="font-mono-data text-xs font-semibold text-slate-200">{u.npsn}</Td>
              <Td className="font-mono-data text-xs text-blue-300">{u.id_transaksi}</Td>
              <Td className="max-w-[220px] truncate text-slate-100">{u.sekolah}</Td>
              <Td className="text-xs">{u.propinsi}</Td>
              <Td className="text-xs">{u.kabupaten_kota}</Td>
              <Td className="text-xs">{u.kecamatan || "-"}</Td>
              <Td className="font-mono-data text-xs">{u.unit}</Td>
              <Td><KorwilBadge value={u.korwil} /></Td>
              <Td className={`font-mono-data text-xs ${isLate(u) ? "text-red-300" : ""}`}>
                {fmtDate(u.jadwal)}
              </Td>
              <Td className="max-w-[140px] truncate text-xs">
                {u.teknisi_pusat_nama || <span className="text-slate-500">-</span>}
              </Td>
              <Td className="font-mono-data text-[11px] text-slate-400">
                {u.teknisi_pusat_telepon || "-"}
              </Td>
              <Td><StatusBadge value={u.status_pusat} /></Td>
              {canManage && <Td><div className="flex items-center gap-1.5">
                {!archiveView && <button data-testid={"jadwal-edit-" + u.id_transaksi} onClick={() => openEdit(u)} className="flex items-center gap-1 rounded border border-blue-500/50 px-2 py-1 text-[11px] font-semibold text-blue-300 hover:bg-blue-500/10"><Pencil className="h-3 w-3" /> Edit</button>}
                {archiveView
                  ? <button onClick={() => toggleArchive(u, true)} className="flex items-center gap-1 rounded border border-emerald-500/50 px-2 py-1 text-[11px] font-semibold text-emerald-300 hover:bg-emerald-500/10"><RotateCcw className="h-3 w-3" /> Aktifkan</button>
                  : <button onClick={() => toggleArchive(u, false)} className="flex items-center gap-1 rounded border border-amber-500/50 px-2 py-1 text-[11px] font-semibold text-amber-300 hover:bg-amber-500/10"><Archive className="h-3 w-3" /> Arsip</button>}
                <button onClick={() => openHistory(u)} title="Riwayat perubahan" className="flex items-center gap-1 rounded border border-slate-600 px-2 py-1 text-[11px] font-semibold text-slate-300 hover:bg-slate-800"><Clock3 className="h-3 w-3" /> Riwayat</button>
              </div></Td>}
            </tr>
          ))}
        </tbody>
      </TableShell>
      <Pagination page={res.page || page} perPage={perPage} total={res.total} onPage={setPage} onPerPage={(n) => { setPerPage(n); setPage(1); }} testId="jadwal-pagination" />

      {historyItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true" aria-label="Riwayat perubahan jadwal pusat">
          <div className="w-full max-w-2xl rounded-xl border border-slate-700 bg-slate-900 p-5 shadow-2xl">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div><h3 className="text-lg font-semibold text-white">Riwayat Perubahan Jadwal</h3><p className="mt-1 text-xs text-slate-400">{historyItem.id_transaksi} · {historyItem.sekolah}</p></div>
              <button onClick={() => setHistoryItem(null)} aria-label="Tutup" className="rounded p-1 text-slate-400 hover:bg-slate-800 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            {historyLoading ? <p className="py-6 text-center text-sm text-slate-400">Memuat riwayat...</p> : historyRows.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">Belum ada riwayat perubahan untuk jadwal ini.</p> : (
              <div className="max-h-[60vh] space-y-3 overflow-y-auto">
                {historyRows.map((row, index) => (
                  <div key={row._id || index} className="rounded border border-slate-700 bg-slate-950 p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2"><span className="text-sm font-semibold text-white">{row.action === "EDIT_TEKNISI_PUSAT" ? "Perubahan Teknisi Pusat" : row.action === "ARSIPKAN" ? "Jadwal Diarsipkan" : "Jadwal Diaktifkan Kembali"}</span><span className="text-xs text-slate-400">{row.created_at ? new Date(row.created_at).toLocaleString("id-ID") : "-"}</span></div>
                    <p className="mt-1 text-xs text-slate-400">Oleh: {row.actor_name || row.actor_email || "-"} · {row.actor_role || "-"}</p>
                    {row.action === "EDIT_TEKNISI_PUSAT" && <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2"><div className="rounded bg-slate-900 p-2"><p className="text-[10px] uppercase text-slate-500">Sebelum</p><p className="text-xs text-slate-300">{row.before?.teknisi_pusat_nama || "-"} · {row.before?.teknisi_pusat_kode || "-"}</p><p className="text-xs text-slate-400">{row.before?.teknisi_pusat_telepon || "-"}</p></div><div className="rounded bg-slate-900 p-2"><p className="text-[10px] uppercase text-slate-500">Sesudah</p><p className="text-xs text-slate-300">{row.after?.teknisi_pusat_nama || "-"} · {row.after?.teknisi_pusat_kode || "-"}</p><p className="text-xs text-slate-400">{row.after?.teknisi_pusat_telepon || "-"}</p></div></div>}
                  </div>
                ))}
              </div>
            )}
            <div className="mt-4 flex justify-end"><button onClick={() => setHistoryItem(null)} className="rounded border border-slate-700 px-3 py-2 text-xs font-semibold text-slate-300 hover:bg-slate-800">Tutup</button></div>
          </div>
        </div>
      )}
      {editItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true" aria-label="Edit teknisi jadwal pusat">
          <div className="w-full max-w-xl rounded-xl border border-slate-700 bg-slate-900 p-5 shadow-2xl">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div><h3 className="text-lg font-semibold text-white">Edit Teknisi Jadwal Pusat</h3><p className="mt-1 text-xs text-slate-400">{editItem.id_transaksi} · {editItem.sekolah}</p></div>
              <button onClick={() => setEditItem(null)} aria-label="Tutup" className="rounded p-1 text-slate-400 hover:bg-slate-800 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <label className="mb-1 block text-xs font-semibold text-slate-300">Cari Teknisi Pusat (Kode atau Nama)</label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <input autoFocus data-testid="jadwal-edit-kode" value={editCode} onChange={(e) => { setEditCode(e.target.value); setLookupTech(null); setSearchResults([]); setShowTechList(false); }} placeholder="Cari kode atau nama teknisi pusat" className="min-w-0 flex-1 rounded border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white outline-none focus:border-blue-500" />
              <button type="button" data-testid="jadwal-tech-list-toggle" onClick={async () => {
                if (showTechList) { setShowTechList(false); setSearchResults([]); return; }
                setShowTechList(true); setLookupState("loading"); setSearchResults([]);
                try {
                  const { data } = await api.get("/jadwal/teknisi-cari", { params: { id_transaksi: editItem.id_transaksi, list_all: true } });
                  setSearchResults(data?.items || []);
                  setLookupState((data?.items || []).length ? "found" : "not-found");
                } catch (e) {
                  setLookupState(e?.response?.status === 403 ? "forbidden" : "error");
                }
              }} className="shrink-0 rounded border border-blue-500/60 px-3 py-2 text-xs font-semibold text-blue-300 hover:bg-blue-500/10">{showTechList ? "Tutup Daftar" : "List Teknisi"}</button>
            </div>
            {searchResults.length > 0 && (
              <div className="mt-2 max-h-52 overflow-y-auto rounded border border-slate-700 bg-slate-950">
                {searchResults.map((tech) => (
                  <button key={tech.kode_teknisi} type="button" onClick={() => chooseTechnician(tech)} className="flex w-full items-start justify-between gap-3 border-b border-slate-800 px-3 py-2 text-left last:border-b-0 hover:bg-slate-800">
                    <span><span className="block text-sm font-semibold text-white">{tech.nama || "Nama belum tersedia"}</span><span className="block font-mono text-xs text-blue-300">{tech.kode_teknisi}</span></span>
                    <span className="text-right text-xs text-slate-400">{tech.telepon || "-"}<span className="block">{tech.korda || ""}</span></span>
                  </button>
                ))}
              </div>
            )}
            {lookupState === "loading" && <p className="mt-2 text-xs text-slate-400">Mencari teknisi berdasarkan kode/nama...</p>}
            {lookupState === "not-found" && <p className="mt-2 text-xs text-amber-300">Teknisi aktif tidak ditemukan pada Korwil jadwal ini. Coba kode atau nama lain, atau gunakan tombol List Teknisi.</p>}
            {lookupState === "error" && <p className="mt-2 text-xs text-red-400">Pencarian teknisi gagal. Coba lagi.</p>}
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div><label className="mb-1 block text-xs font-semibold text-slate-300">Nama Teknisi Baru</label><div className="rounded border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-200">{lookupTech?.nama || "-"}</div></div>
              <div><label className="mb-1 block text-xs font-semibold text-slate-300">No. HP Teknisi Baru</label><div className="rounded border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-200">{lookupTech?.telepon || "-"}</div></div>
            </div>
            {lookupTech && <p className="mt-2 flex items-center gap-1 text-xs text-emerald-400"><Check className="h-3.5 w-3.5" /> Dipilih: {lookupTech.nama} ({lookupTech.kode_teknisi})</p>}
            <p className="mt-3 text-xs text-slate-400">Hanya kode, nama, dan nomor teknisi pusat yang akan berubah. Data sekolah, jadwal, status instalasi, teknisi aktual, BAPP, dan pembayaran tetap dipertahankan.</p>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setEditItem(null)} className="rounded border border-slate-700 px-3 py-2 text-xs font-semibold text-slate-300 hover:bg-slate-800">Batal</button>
              <button data-testid="jadwal-edit-save" disabled={saving || !lookupTech} onClick={saveEdit} className="flex items-center gap-1.5 rounded bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50"><Check className="h-3.5 w-3.5" /> {saving ? "Menyimpan..." : "Simpan Perubahan"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
